import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";
import StatementPasswordReveal from "@/components/StatementPasswordReveal";
import { useAuth } from "@/contexts/AuthContext";
import { Sensitive } from "@/components/security/Sensitive";
import { ChevronLeft, Pencil, Receipt, FileText, Banknote, Sparkles, ScrollText, Layers, Download, BookOpen } from "lucide-react";
import { jsPDF } from "jspdf";
import CardOffersSection from "@/components/CardOffersSection";
import RewardValueCard from "@/components/RewardValueCard";
import TxnTypeBadge from "@/components/TxnTypeBadge";

function KV({ label, value, mono, className = "" }) {
    return (
        <div className={`flex flex-col ${className}`}>
            <div className="eyebrow mb-1">{label}</div>
            <div className={`text-sm text-stone-900 ${mono ? "font-mono-figure tabular-nums" : ""}`}>
                {value == null || value === "" ? <span className="text-stone-400">—</span> : value}
            </div>
        </div>
    );
}

function healthTone(score) {
    if (score == null) return "text-stone-500 border-stone-200 bg-stone-50";
    if (score >= 90) return "text-emerald-700 border-emerald-200 bg-emerald-50";
    if (score >= 70) return "text-blue-700 border-blue-200 bg-blue-50";
    if (score >= 50) return "text-amber-700 border-amber-200 bg-amber-50";
    return "text-red-700 border-red-200 bg-red-50";
}

export default function CardDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { hasRole } = useAuth();
    const [card, setCard] = useState(null);
    const [score, setScore] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [statements, setStatements] = useState([]);
    const [payments, setPayments] = useState([]);
    const [audit, setAudit] = useState([]);

    const load = useCallback(async () => {
        try {
            const [c, e, s, p, sc] = await Promise.all([
                api.get(`/cards/${id}`),
                api.get(`/expenses`, { params: { card_id: id } }),
                api.get(`/statements`, { params: { card_id: id } }),
                api.get(`/payments`, { params: { card_id: id } }),
                api.get(`/analytics/card-scores`),
            ]);
            setCard(c.data);
            setExpenses(e.data || []);
            setStatements(s.data || []);
            setPayments(p.data || []);
            const s0 = (sc.data.cards || []).find((x) => x.card_id === id);
            setScore(s0 || null);
            // Audit is admin/finance only — fetch quietly
            if (hasRole("admin", "finance")) {
                try {
                    const a = await api.get(`/audit-logs`, { params: { entity_id: id, limit: 30 } });
                    setAudit(a.data || []);
                } catch (err) {
                    console.warn("[CardDetail] audit fetch failed", err?.message || err);
                }
            }
        } catch (e) {
            console.error("[CardDetail] load failed", e);
        }
    }, [id, hasRole]);

    useEffect(() => { load(); }, [load]);

    const utilization = useMemo(() => {
        if (!card?.credit_limit || !score) return null;
        return score.utilization_pct;
    }, [card, score]);

    if (!card) {
        return <div className="text-sm text-stone-500">Loading card details…</div>;
    }

    const util = utilization ?? 0;
    const utilBarTone = util >= 90 ? "bg-red-500" : util >= 70 ? "bg-amber-500" : util >= 50 ? "bg-blue-500" : "bg-emerald-500";

    const totalExpense = expenses.reduce((a, e) => a + Number(e.amount || 0), 0);
    const totalPaid = payments.reduce((a, p) => a + Number(p.amount_paid || 0), 0);

    const exportPDF = () => {
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        let y = 48;
        // Header bar
        doc.setFillColor(28, 41, 61);
        doc.rect(0, 0, pageW, 90, "F");
        // VAULT wordmark (light on navy)
        doc.setTextColor(248, 250, 252);
        doc.setFont("helvetica", "bold").setFontSize(22);
        doc.text("VAULT", 40, 52, { charSpace: 3 });
        doc.setTextColor(214, 195, 154);
        doc.setFont("helvetica", "normal").setFontSize(9);
        doc.text("CREDIT CARD · CONTROL CENTER · CARD FACT SHEET", 40, 74);
        doc.setFontSize(8).setTextColor(180);
        doc.text(`Generated ${new Date().toLocaleString()}`, pageW - 40, 40, { align: "right" });
        doc.text("VAULT · Owner & Finance Dashboard", pageW - 40, 56, { align: "right" });
        y = 120;
        // Card headline
        doc.setTextColor(28, 25, 23);
        doc.setFont("helvetica", "bold").setFontSize(20);
        doc.text(`${card.bank_name} · ${card.card_name}`, 40, y); y += 22;
        doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(120);
        doc.text(`•••• ${card.last_4}   ·   ${card.card_holder_name || "—"}   ·   ${card.status}`, 40, y); y += 26;
        // KPI row
        const kpi = [
            ["Outstanding", fmtMoney(score?.outstanding || 0)],
            ["Credit Limit", fmtMoney(card.credit_limit || 0)],
            ["Utilization", `${util}%`],
            ["Health", score ? `${score.health_score} ${score.health_label}` : "—"],
        ];
        const kw = (pageW - 80) / 4;
        kpi.forEach(([k, v], i) => {
            doc.setFontSize(8).setTextColor(140);
            doc.text(k.toUpperCase(), 40 + i * kw, y);
            doc.setFontSize(14).setTextColor(28, 25, 23).setFont("helvetica", "bold");
            doc.text(String(v), 40 + i * kw, y + 20);
            doc.setFont("helvetica", "normal");
        });
        y += 60;
        doc.setDrawColor(230); doc.line(40, y, pageW - 40, y); y += 22;
        // Details grid
        const details = [
            ["Card Type", card.card_type], ["Ownership", card.ownership_tag],
            ["Network", card.card_network], ["Category", card.category],
            ["Statement Gen Day", card.statement_gen_day], ["Payment Due Day", card.payment_due_day],
            ["Expiry", card.expiry_month && card.expiry_year ? `${String(card.expiry_month).padStart(2, "0")}/${card.expiry_year}` : "—"],
            ["Annual Fee", fmtMoney(card.annual_fee || 0)],
            ["Registered Mobile", card.registered_mobile || "—"], ["Registered Email", card.registered_email || "—"],
            ["Toll-Free", card.toll_free || "—"], ["RM", card.rm_name || "—"],
        ];
        doc.setFontSize(11).setFont("helvetica", "bold").setTextColor(28, 25, 23);
        doc.text("Details", 40, y); y += 16;
        doc.setFont("helvetica", "normal").setFontSize(9);
        details.forEach((row, idx) => {
            const col = idx % 2;
            const rowY = y + Math.floor(idx / 2) * 18;
            doc.setTextColor(140); doc.text(row[0], 40 + col * (pageW / 2 - 20), rowY);
            doc.setTextColor(28, 25, 23); doc.text(String(row[1] || "—"), 170 + col * (pageW / 2 - 20), rowY);
        });
        y += Math.ceil(details.length / 2) * 18 + 20;
        // Best For / Notes
        if (card.best_for || card.avoid_for || card.remarks) {
            doc.setDrawColor(230); doc.line(40, y, pageW - 40, y); y += 18;
            doc.setFont("helvetica", "bold").setFontSize(11); doc.text("Best use & notes", 40, y); y += 14;
            doc.setFont("helvetica", "normal").setFontSize(9);
            if (card.best_for) { doc.setTextColor(140); doc.text("Best for:", 40, y); doc.setTextColor(28, 25, 23); doc.text(doc.splitTextToSize(card.best_for, pageW - 130), 100, y); y += 18; }
            if (card.avoid_for) { doc.setTextColor(140); doc.text("Avoid for:", 40, y); doc.setTextColor(28, 25, 23); doc.text(doc.splitTextToSize(card.avoid_for, pageW - 130), 100, y); y += 18; }
            if (card.remarks) { doc.setTextColor(140); doc.text("Remarks:", 40, y); doc.setTextColor(28, 25, 23); doc.text(doc.splitTextToSize(card.remarks, pageW - 130), 100, y); y += 18; }
        }
        // Footer
        doc.setDrawColor(230); doc.line(40, 800, pageW - 40, 800);
        doc.setTextColor(160).setFontSize(8);
        doc.text("VAULT · Confidential Finance Report · Credit Card Control Center", 40, 815);
        doc.text("Sensitive fields (CVV, OTP, PIN, full card number, statement password) are excluded by policy.", 40, 828);
        doc.save(`Card_${card.bank_name}_${card.last_4}_FactSheet.pdf`);
    };

    return (
        <div data-testid="card-detail-page">
            {/* Header */}
            <div className="mb-6">
                <button onClick={() => nav("/cards")} className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 transition-colors mb-3">
                    <ChevronLeft size={14} /> Back to cards
                </button>
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="eyebrow">Credit Card 360°</div>
                        <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                            {card.bank_name} · {card.card_name}
                        </h1>
                        <div className="text-sm text-stone-500 mt-2 flex items-center gap-3 flex-wrap">
                            <Sensitive level="highly_sensitive" as="span" className="font-mono-figure" data-testid="card-pan-detail">•••• {card.last_4}</Sensitive>
                            <span>·</span>
                            <span>{card.card_holder_name || "—"}</span>
                            <span>·</span>
                            <StatusPill status={card.status} />
                            {card.has_statement_password && (
                                <StatementPasswordReveal cardId={card.id} size="xs" label="Reveal statement password" />
                            )}
                        </div>
                    </div>
                    {hasRole("admin", "finance") && (
                        <div className="flex items-center gap-2">
                            <Button onClick={exportPDF} variant="outline" size="sm" data-testid="detail-export-pdf">
                                <Download size={13} className="mr-1.5" /> Download PDF
                            </Button>
                            <Button onClick={() => nav(`/cards/${id}`)} variant="outline" size="sm" data-testid="detail-edit-btn">
                                <Pencil size={13} className="mr-1.5" /> Edit card
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Health & utilization strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="kpi-card">
                    <div className="eyebrow">Outstanding</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(score?.outstanding ?? 0)}</div>
                    <div className="text-[11px] text-stone-500 mt-1">of {fmtMoney(card.credit_limit || 0)}</div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">Utilization</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{util}%</div>
                    <div className="w-full h-1.5 bg-stone-100 rounded-full overflow-hidden mt-2">
                        <div className={`h-full ${utilBarTone} transition-all duration-500`} style={{ width: `${Math.min(100, util)}%` }} />
                    </div>
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">Health · Risk</div>
                    <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${healthTone(score?.health_score)}`}>
                            {score ? `${score.health_score} · ${score.health_label}` : "—"}
                        </span>
                        {score && (
                            <span className="text-[11px] text-stone-500">
                                Risk: <span className="font-medium">{score.risk_label}</span>
                            </span>
                        )}
                    </div>
                    {score?.factors?.length > 0 && (
                        <div className="text-[10.5px] text-stone-500 mt-2 leading-relaxed">
                            {score.factors.slice(0, 2).join(" · ")}
                        </div>
                    )}
                </div>
                <div className="kpi-card">
                    <div className="eyebrow">This year on this card</div>
                    <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(totalExpense)}</div>
                    <div className="text-[11px] text-stone-500 mt-1">{expenses.length} expenses · {payments.length} payments</div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="mt-4">
                <TabsList>
                    <TabsTrigger value="overview" data-testid="tab-overview"><Layers size={12} className="mr-1" />Overview</TabsTrigger>
                    <TabsTrigger value="expenses" data-testid="tab-expenses"><Receipt size={12} className="mr-1" />Expenses <span className="ml-1.5 text-stone-500">{expenses.length}</span></TabsTrigger>
                    <TabsTrigger value="statements" data-testid="tab-statements"><FileText size={12} className="mr-1" />Statements <span className="ml-1.5 text-stone-500">{statements.length}</span></TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments"><Banknote size={12} className="mr-1" />Payments <span className="ml-1.5 text-stone-500">{payments.length}</span></TabsTrigger>
                    <TabsTrigger value="benefits" data-testid="tab-benefits"><Sparkles size={12} className="mr-1" />Benefits</TabsTrigger>
                    {hasRole("admin", "finance") && (
                        <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText size={12} className="mr-1" />Audit</TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="overview">
                    <div className="form-section grid grid-cols-2 md:grid-cols-4 gap-5">
                        <KV label="Bank Name" value={card.bank_name} />
                        <KV label="Card Name" value={card.card_name} />
                        <KV label="Card Holder" value={card.card_holder_name} />
                        <KV label="Ownership" value={card.ownership_tag} />
                        <KV label="Card Type" value={card.card_kind || "Credit Card"} />
                        <KV label="Card Level" value={card.card_level} />
                        <KV label="Card Purpose" value={card.card_type} />
                        <KV label="Network" value={card.card_network} />
                        <KV label="Category" value={card.category} />
                        <KV label="Priority" value={card.priority} />
                        <KV label="Credit Limit" value={fmtMoney(card.credit_limit)} mono />
                        <KV label="Cash Limit" value={fmtMoney(card.cash_limit)} mono />
                        <KV label="Available Limit" value={fmtMoney((card.credit_limit || 0) - (score?.outstanding || 0))} mono />
                        <KV label="Utilization" value={`${util}%`} mono />
                        <KV label="Billing Date (day)" value={card.billing_end_day} mono />
                        <KV label="Statement Gen Day" value={card.statement_gen_day} mono />
                        <KV label="Payment Due Day" value={card.payment_due_day} mono />
                        <KV label="Expiry" value={card.expiry_month && card.expiry_year ? `${String(card.expiry_month).padStart(2, "0")}/${card.expiry_year}` : "—"} mono />
                        <KV label="Registered Mobile" value={card.registered_mobile} mono />
                        <KV label="Registered Email" value={card.registered_email} />
                        <KV label="Toll-Free" value={card.toll_free} mono />
                        <KV label="RM Name" value={card.rm_name} />
                    </div>
                    {(card.opening_outstanding > 0 || card.opening_remarks) && (
                        <div className="form-section mt-4 grid grid-cols-2 md:grid-cols-4 gap-5">
                            <div className="col-span-full eyebrow">Opening balance (pre-dashboard)</div>
                            <KV label="Opening Outstanding" value={fmtMoney(card.opening_outstanding)} mono />
                            <KV label="Opening Date" value={fmtDate(card.opening_outstanding_date)} />
                            <KV label="Opening Statement Month" value={card.opening_statement_month} />
                            <KV label="Remarks" value={card.opening_remarks} className="col-span-2" />
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="expenses">
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="dense-table w-full">
                            <thead><tr><th>Date</th><th>Merchant</th><th>Category</th><th>Business/Personal</th><th>Type</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
                            <tbody>
                                {expenses.length === 0 && <tr><td colSpan={7} className="text-center text-stone-500 py-8">No expenses recorded on this card yet.</td></tr>}
                                {expenses.map((e) => {
                                    const isCredit = (e.transaction_type || "Debit") === "Credit";
                                    return (
                                        <tr key={e.id} data-testid={`carddetail-exp-row-${e.id}`}>
                                            <td className="tabular-nums">{fmtDate(e.date)}</td>
                                            <td>{e.merchant}</td>
                                            <td>{e.category}</td>
                                            <td>{e.business_personal}</td>
                                            <td><TxnTypeBadge value={e.transaction_type} /></td>
                                            <td className={`text-right tabular-nums font-medium ${isCredit ? "text-emerald-700" : ""}`}>
                                                {isCredit ? "+ " : ""}{fmtMoney(e.amount)}
                                            </td>
                                            <td><StatusPill status={e.approval_status} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="statements">
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="dense-table w-full">
                            <thead><tr><th>Month</th><th>Stmt Date</th><th>Due Date</th><th className="text-right">Opening</th><th className="text-right">Closing</th><th className="text-right">Total Due</th><th className="text-right">Min Due</th><th>Status</th><th></th></tr></thead>
                            <tbody>
                                {statements.length === 0 && <tr><td colSpan={9} className="text-center text-stone-500 py-8">No statements uploaded yet for this card.</td></tr>}
                                {statements.map((s) => (
                                    <tr key={s.id} data-testid={`carddetail-stmt-row-${s.id}`}>
                                        <td>{s.statement_month}</td>
                                        <td className="tabular-nums">{fmtDate(s.statement_date)}</td>
                                        <td className="tabular-nums">{fmtDate(s.due_date)}</td>
                                        <td className="text-right tabular-nums">{fmtMoney(s.opening_balance)}</td>
                                        <td className="text-right tabular-nums font-medium">{fmtMoney(s.closing_balance)}</td>
                                        <td className="text-right tabular-nums">{fmtMoney(s.total_amount_due)}</td>
                                        <td className="text-right tabular-nums">{fmtMoney(s.min_amount_due)}</td>
                                        <td><StatusPill status={s.status || "Uploaded"} /></td>
                                        <td>
                                            <Link
                                                to={`/statements/${s.id}/ledger`}
                                                title="Ledger"
                                                className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 text-xs"
                                                data-testid={`carddetail-stmt-ledger-${s.id}`}
                                            >
                                                <BookOpen size={12} /> Ledger
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="payments">
                    <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                        <table className="dense-table w-full">
                            <thead><tr><th>Date</th><th>Month</th><th className="text-right">Amount</th><th>Mode</th><th>UTR</th><th>Posted</th></tr></thead>
                            <tbody>
                                {payments.length === 0 && <tr><td colSpan={6} className="text-center text-stone-500 py-8">No payments recorded on this card yet.</td></tr>}
                                {payments.map((p) => (
                                    <tr key={p.id}>
                                        <td className="tabular-nums">{fmtDate(p.payment_date)}</td>
                                        <td>{p.statement_month}</td>
                                        <td className="text-right tabular-nums font-medium">{fmtMoney(p.amount_paid)}</td>
                                        <td>{p.payment_mode}</td>
                                        <td className="font-mono-figure text-[11px]">{p.utr || "—"}</td>
                                        <td>{p.posted ? <span className="text-emerald-700 text-xs">Reflected</span> : <span className="text-amber-700 text-xs">Pending</span>}</td>
                                    </tr>
                                ))}
                                {payments.length > 0 && (
                                    <tr><td colSpan={2} className="font-medium text-stone-900">Total paid</td><td className="text-right tabular-nums font-medium">{fmtMoney(totalPaid)}</td><td colSpan={3}></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="benefits">
                    {/* Real Reward Value tracker — Rewards − Fees = Net Benefit */}
                    <RewardValueCard cardId={id} />
                    {/* Live offers & benefits attached to this card */}
                    <CardOffersSection cardId={id} />
                    <div className="form-section grid grid-cols-1 md:grid-cols-2 gap-5">
                        <KV label="Best For" value={card.best_for} />
                        <KV label="Avoid For" value={card.avoid_for} />
                        <KV label="Reward Program" value={card.reward_program} />
                        <KV label="Reward Points" value={card.reward_points} mono />
                        <KV label="₹ per Point" value={card.point_value != null ? `₹${card.point_value}` : "—"} mono />
                        <KV label="Annual Fee" value={fmtMoney(card.annual_fee)} mono />
                        <KV label="Annual Fee Waiver" value={card.annual_fee_waiver} />
                        <KV label="Waiver Achieved" value={card.annual_fee_waiver_achieved ? "Yes" : "No"} />
                        <KV label="Lounge Access" value={card.lounge_access ? "Yes" : "No"} />
                        <KV label="Fuel Surcharge Waiver" value={card.fuel_surcharge_waiver ? "Yes" : "No"} />
                        <KV label="Forex Markup" value={card.forex_markup ? `${card.forex_markup}%` : "—"} mono />
                        <div className="col-span-full">
                            <KV label="Remarks" value={card.remarks} />
                        </div>
                    </div>
                </TabsContent>

                {hasRole("admin", "finance") && (
                    <TabsContent value="audit">
                        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                            <table className="dense-table w-full">
                                <thead><tr><th>When</th><th>Action</th><th>User</th><th>Details</th></tr></thead>
                                <tbody>
                                    {audit.length === 0 && <tr><td colSpan={4} className="text-center text-stone-500 py-8">No audit entries yet for this card.</td></tr>}
                                    {audit.map((a) => (
                                        <tr key={a.id}>
                                            <td className="tabular-nums text-[11px]">{a.timestamp ? new Date(a.timestamp).toLocaleString() : "—"}</td>
                                            <td>{a.action}</td>
                                            <td>{a.user_name || a.user_email || "—"}</td>
                                            <td className="text-[11px] text-stone-600">{a.details ? JSON.stringify(a.details) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
